import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCondominio } from '../contexts/CondominioContext';
import {
  Users,
  Building2,
  UserCheck,
  Package,
  AlertTriangle,
  TrendingUp,
  Clock,
  CheckCircle
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const Dashboard = () => {
  const { user } = useAuth();
  const { getCondominioId, isMorador } = useCondominio();
  const navigate = useNavigate();
  const [versaoSistema, setVersaoSistema] = useState(null);
  
  const condominioId = getCondominioId();

  const { data: dashboardData, isLoading } = useQuery(
    ['dashboard', condominioId],
    () => api.get('/relatorios/dashboard').then(res => res.data.data),
    {
      refetchInterval: 30000,
      retry: false,
      enabled: !!(condominioId || user?.tipo === 'administrador'),
      onError: (error) => {
        console.error('Erro ao carregar dashboard:', error);
      }
    }
  );

  // Buscar versão do sistema
  useEffect(() => {
    const buscarVersao = async () => {
      try {
        const response = await api.get('/versao/atual/web');
        if (response.data.success && response.data.data) {
          setVersaoSistema(response.data.data);
        }
      } catch (error) {
        console.error('Erro ao buscar versão:', error);
        // Em caso de erro, definir versão padrão
        setVersaoSistema({
          versao: '1.0.0',
          descricao: 'Versão inicial',
          data_lancamento: new Date()
        });
      }
    };
    buscarVersao();
  }, []);

  const stats = [
    {
      name: 'Unidades',
      value: dashboardData?.totais?.unidades || 0,
      icon: Building2,
      color: 'bg-indigo-500'
    },
    {
      name: 'Moradores',
      value: dashboardData?.totais?.moradores || 0,
      icon: Users,
      color: 'bg-green-500'
    },
    {
      name: 'Visitantes Hoje',
      value: dashboardData?.hoje?.visitantes || 0,
      icon: UserCheck,
      color: 'bg-blue-500'
    },
    {
      name: 'Encomendas Pendentes',
      value: dashboardData?.hoje?.encomendasPendentes || 0,
      icon: Package,
      color: 'bg-yellow-500'
    },
    {
      name: 'Ocorrências Abertas',
      value: dashboardData?.hoje?.ocorrenciasAbertas || 0,
      icon: AlertTriangle,
      color: 'bg-red-500'
    },
    {
      name: 'Usuários Ativos',
      value: dashboardData?.hoje?.usuariosAtivos || 0,
      icon: Users,
      color: 'bg-emerald-600'
    }
  ];

  // Mapear atividades recentes do backend para o formato do frontend
  const recentActivities = (dashboardData?.atividadesRecentes || []).map(activity => {
    let icon, color;
    switch(activity.type) {
      case 'visitante':
        icon = UserCheck;
        color = 'text-blue-600';
        break;
      case 'encomenda':
        icon = Package;
        color = 'text-green-600';
        break;
      case 'ocorrencia':
        icon = AlertTriangle;
        color = 'text-red-600';
        break;
      default:
        icon = Clock;
        color = 'text-gray-600';
    }
    return {
      id: activity.id,
      type: activity.type,
      message: activity.message,
      time: activity.time,
      icon,
      color
    };
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Olá, {user?.nome?.split(' ')[0]}! 👋
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {user?.tipo === 'administrador' && !getCondominioId() 
            ? 'Você está acessando como administrador. Cadastre um condomínio para começar.'
            : 'Aqui está um resumo das atividades do seu condomínio hoje.'}
        </p>
        {user?.tipo === 'administrador' && !getCondominioId() && (
          <div className="mt-4">
            <button
              onClick={() => navigate('/condominios')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Building2 className="h-4 w-4 mr-2" />
              Cadastrar Condomínio
            </button>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      {getCondominioId() || user?.tipo === 'administrador' ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.name} className="card">
                <div className="card-body">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className={`p-3 rounded-md ${stat.color}`}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          {stat.name}
                        </dt>
                        <dd className="text-2xl font-semibold text-gray-900">
                          {stat.value}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card">
          <div className="card-body text-center py-8">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500 mb-4">Você não está vinculado a nenhum condomínio.</p>
            <p className="text-sm text-gray-400">Entre em contato com o administrador do sistema.</p>
          </div>
        </div>
      )}

      {(getCondominioId() || user?.tipo === 'administrador') && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Recent Activities */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Atividades Recentes</h3>
            </div>
            <div className="card-body">
              {recentActivities.length > 0 ? (
                <div className="flow-root">
                  <ul className="-mb-8">
                    {recentActivities.map((activity, activityIdx) => {
                      const Icon = activity.icon;
                      return (
                        <li key={activity.id}>
                          <div className="relative pb-8">
                            {activityIdx !== recentActivities.length - 1 ? (
                              <span
                                className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                                aria-hidden="true"
                              />
                            ) : null}
                            <div className="relative flex space-x-3">
                              <div>
                                <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${activity.color}`}>
                                  <Icon className="h-5 w-5" />
                                </span>
                              </div>
                              <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                                <div>
                                  <p className="text-sm text-gray-500">{activity.message}</p>
                                </div>
                                <div className="text-right text-sm whitespace-nowrap text-gray-500">
                                  {activity.time}
                                </div>
                              </div>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">Nenhuma atividade registrada hoje</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          {getCondominioId() && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900">Ações Rápidas</h3>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-2 gap-4">
                  <button 
                    onClick={() => navigate('/visitantes')}
                    className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <UserCheck className="h-8 w-8 text-blue-600 mb-2" />
                    <span className="text-sm font-medium text-gray-900">Registrar Visitante</span>
                  </button>
                  <button 
                    onClick={() => navigate('/encomendas')}
                    className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Package className="h-8 w-8 text-yellow-600 mb-2" />
                    <span className="text-sm font-medium text-gray-900">Receber Encomenda</span>
                  </button>
                  <button 
                    onClick={() => navigate('/ocorrencias')}
                    className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <AlertTriangle className="h-8 w-8 text-red-600 mb-2" />
                    <span className="text-sm font-medium text-gray-900">Reportar Ocorrência</span>
                  </button>
                  <button 
                    onClick={() => navigate('/moradores')}
                    className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Users className="h-8 w-8 text-green-600 mb-2" />
                    <span className="text-sm font-medium text-gray-900">Cadastrar Morador</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Weekly Summary */}
      {(getCondominioId() || user?.tipo === 'administrador') && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Resumo da Semana</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              <div className="text-center">
                <div className="text-2xl font-bold text-indigo-600">
                  {dashboardData?.totais?.unidades || 0}
                </div>
                <div className="text-sm text-gray-500">Unidades</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {dashboardData?.totais?.moradores || 0}
                </div>
                <div className="text-sm text-gray-500">Moradores</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {dashboardData?.semana?.visitantes || 0}
                </div>
                <div className="text-sm text-gray-500">Visitantes na semana</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">
                  {dashboardData?.semana?.encomendas || 0}
                </div>
                <div className="text-sm text-gray-500">Encomendas na semana</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {dashboardData?.semana?.ocorrencias || 0}
                </div>
                <div className="text-sm text-gray-500">Ocorrências na semana</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Versão do Sistema */}
      {versaoSistema && (
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-400">
            Versão {versaoSistema.versao}
          </p>
        </div>
      )}

    </div>
  );
};

export default Dashboard;
